import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './styles/index.css';
import { apiRequest } from './utils/api';
import { isAnswerEngineOnly } from './utils/plan';
import { AuthProvider2, useAuth2 } from './contexts2/AuthContext2';
import { ChatStateProvider, useChatState } from './contexts/ChatStateContext';
import { useAnalysisState } from './hooks2/useAnalysisState';
import { useRunNewAnalysis } from './hooks2/useRunNewAnalysis';
import { useSmartContext } from './hooks2/useSmartContext';
import { useChatStateActions } from './hooks2/useChatStateActions';
import { ChatProvider } from './contexts/ChatContext';
import md5 from 'md5';
import { verifyChildRoutes, siteSelectorRoute } from './verify/routeConfig';
import ErrorBoundary from './verify/components/ui/ErrorBoundary';

// Import topic analysis helpers
import {
  extractKeywordsForClustering,
  deduplicateKeywords,
  performMLClustering
} from './components2/analysis/utils/topicsHelpers';

// Import filter utilities
import { getAnalysisStorageKeys, clearAnalysisFiltersForEntity } from './components2/analysis/utils/analysisFilters';

// Layout Components
import Sidebar from './components2/layout/Sidebar';
import MobileHeader from './components2/layout/MobileHeader';

// Chat Components
import { FloatingChatButton, ChatOverlay, ChatSidebar } from './components2/chat';

// UI Components
import ScrollToTopButton from './components2/ui/ScrollToTopButton';

// Lazy load heavy layer components
const AnalysisLayer = React.lazy(() => import('./components2/layers/AnalysisLayer'));
const PerformanceLayer = React.lazy(() => import('./components2/layers/PerformanceLayer'));
const ToolsLayer = React.lazy(() => import('./components2/layers/ToolsLayer'));

// Lazy load public page components
const AuthCallback = React.lazy(() => import('./components2/AuthCallback'));

const SubscriptionSuccess = React.lazy(() => import('./components2/SubscriptionSuccess'));
const SubscriptionDetails = React.lazy(() => import('./components2/SubscriptionDetails'));

// Lazy load Verify page (QueryBurst Verify - standalone RAG interface)
const PageDetailsPage = React.lazy(() => import('./verify/PageDetailsPage'));
const VerifyLoginScreen = React.lazy(() => import('./verify/components/VerifyLoginScreen'));
const SignupScreen = React.lazy(() => import('./verify/components/SignupScreen'));
const AppShell = React.lazy(() => import('./verify/components/AppShell'));
const SubscriptionPage = React.lazy(() => import('./verify/components/SubscriptionPage'));
const IntegrationsPage = React.lazy(() => import('./verify/components/IntegrationsPage'));
const AgentServePage = React.lazy(() => import('./verify/components/AgentServePage'));
const SearchConsolePage = React.lazy(() => import('./verify/components/gsc-explorer/SearchConsolePage'));
const GscReport = React.lazy(() => import('./verify/components/gsc-explorer/GscReport'));
const HomePage = React.lazy(() => import('./verify/components/HomePage'));
const AnswerEngineShell = React.lazy(() => import('./persona/AnswerEngineShell'));
const WebsiteListPage = React.lazy(() => import('./persona/WebsiteListPage'));
const PersonaListPage = React.lazy(() => import('./persona/PersonaListPage'));
const PersonaChatPage = React.lazy(() => import('./persona/PersonaChatPage'));
const StatsReport = React.lazy(() => import('./persona/StatsReport'));

const FullPlatformGate = ({ children, authStatus }) => {
  if (authStatus.isAuthenticated && isAnswerEngineOnly(authStatus.user)) return <Navigate to="/home" replace />;
  return children;
};

const ProGate = ({ children, authStatus }) => {
  const isDemo = authStatus.isAuthenticated && authStatus.user?.is_demo_user;
  if (isDemo) return <Navigate to="/home?upgrade=pro" replace />;
  return <FullPlatformGate authStatus={authStatus}>{children}</FullPlatformGate>;
};

// Lazy load animation components
const InitialLoading = React.lazy(() => import('./components2/animations/InitialLoading'));


// Lazy load focus route
const Focus = React.lazy(() => import('./components2/focus/Focus'));

// Lazy load domain profile page
const DomainProfilePage = React.lazy(() => import('./components2/profile/DomainProfilePage'));

// Lazy load research intelligence
const ResearchIntelligencePage = React.lazy(() => import('./components2/research/ResearchIntelligencePage'));
const ResearchIntelligenceV2Page = React.lazy(() => import('./components2/research-v2/ResearchIntelligenceV2Page'));
const WebConceptExtractorPage = React.lazy(() => import('./components2/web-extractor/WebConceptExtractorPage'));
const ContentStrategyV2 = React.lazy(() => import('./components2/content-strategyV2/ContentStrategyV2'));
const ArticleEditor = React.lazy(() => import('./components2/content-strategyV2/ArticleEditor'));

// Lazy load DaveGPT Demo (web.run replica)
const WebRunDemoPage = React.lazy(() => import('./webrun/WebRunDemoPage'));


// Lazy load GSC auth popup
const GSCAuthPopup = React.lazy(() => import('./components2/GSCAuthPopup'));

// Strategy components imported in AnalysisLayer where they're used

// Lazy load LLM components
const LLMPortal = React.lazy(() => import('./components2/llm/LLMPortal'));

// Lazy load banners
const DemoBanner = React.lazy(() => import('./components2/banners/DemoBanner'));

// Loading component for lazy-loaded routes
const PageLoadingState = ({ darkMode = false }) => (
  <div className={`flex items-center justify-center h-screen ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-lg">Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const { authStatus, setAuthStatus, logout } = useAuth2();
  const { backendChatState, pendingActions, updateFromBackend, clearActions } = useChatState();
  const navigate = useNavigate();
  const location = useLocation();

  // 🎯 DEBUG: Test ChatStateContext
  useEffect(() => {
    // Only log when we have actual backend state to avoid initial empty state noise
    if (backendChatState) {
      console.log('🎯 [App] ChatStateContext state:', {
        hasBackendState: !!backendChatState,
        chatMode: backendChatState?.mode,
        isOnboarding: backendChatState?.is_onboarding,
        pendingActionsCount: pendingActions.length,
        actions: pendingActions.map(a => a.type)
      });
    }
  }, [backendChatState, pendingActions]);

  // 🎯 SINGLE STATE - Either we have complete app data or we don't
  const [appData, setAppData] = useState(null);
  const [appError, setAppError] = useState(null);
  const initializationAttempted = useRef(false);
  
  const isOnboarding = false;
  
  // 🎯 GLOBAL SCROLL TARGET - For consistent scroll-to-top on navigation
  const mainScrollTarget = useRef(null);

  // 📊 ANALYSIS STATE MANAGEMENT
  const { 
    handlePropertyChange, 
    handleLoadSavedAnalysis,
    handleClearAnalysis 
  } = useAnalysisState(appData, setAppData, isOnboarding);

  // 🚀 PROGRESSIVE ANALYSIS EXECUTION
  const {
    progressiveState,
    analysisResult,
    runNewAnalysis,
    clearNavigationFlag,
    isRunning,
    isComplete,
    hasError,
    hasTimeout,
    historicalDataPolling,
  } = useRunNewAnalysis();

  const openMatrixPortal = () => {};

  // 🧠 SMART CONTEXT DETECTION
  const smartContext = useSmartContext(appData, authStatus);

  // 🎯 TOPIC ANALYSIS STATE MANAGEMENT
  const [topicAnalysisState, setTopicAnalysisState] = useState({
    isRunning: false,
    stage: 'idle', // 'idle', 'running', 'complete', 'error'
    message: '',
    error: null,
    progress: 0
  });

  // 🔍 RENDER TRACKING (after all variables are declared)
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // 🔗 INTEGRATED ANALYSIS HANDLER
  const handleRunNewAnalysis = async (formData) => {
    try {
      
      // Only add selectedProperty if not already provided (for "Run Again" scenarios)
      const completeFormData = {
        ...formData,
        selectedProperty: formData.selectedProperty || appData.selectedProperty
      };
      
      // Run the progressive analysis
      await runNewAnalysis(completeFormData);
      
      // Note: Analysis results will be synced automatically by the useEffect
      
    } catch (error) {
      console.error('🚀 [App] Error in handleRunNewAnalysis:', error);
      // Error is already handled by the hook
    }
  };

  // 🎯 TOPIC ANALYSIS STATE MANAGEMENT FUNCTIONS
  const updateTopicAnalysisState = (updates) => {
    setTopicAnalysisState(prev => ({ ...prev, ...updates }));
  };

  const startTopicAnalysis = () => {
    setTopicAnalysisState({
      isRunning: true,
      stage: 'running',
      message: 'Running ML topic analysis...',
      error: null,
      progress: 0
    });
  };

  const completeTopicAnalysis = () => {
    setTopicAnalysisState({
      isRunning: false,
      stage: 'complete',
      message: 'Topic analysis complete',
      error: null,
      progress: 100
    });
  };

  const errorTopicAnalysis = (error) => {
    setTopicAnalysisState({
      isRunning: false,
      stage: 'error',
      message: 'Topic analysis failed',
      error: error?.message || error || 'Unknown error',
      progress: 0
    });
  };

  // 🎯 MAIN TOPIC ANALYSIS ORCHESTRATION FUNCTION
  const runTopicAnalysis = async () => {
    // console.log('🎯 [App] Starting topic analysis orchestration');
    
    // Extract analysis and meta from appData.temporaryAnalysis
    const analysis = appData?.temporaryAnalysis?.analysis;
    const meta = appData?.temporaryAnalysis?.meta;
    
    if (!analysis || !meta) {
      const error = 'No analysis data available for topic analysis';
      //console.error('❌ [App] Topic analysis failed:', error);
      errorTopicAnalysis(new Error(error));
      throw new Error(error);
    }
    
    startTopicAnalysis(); // Sets stage: 'running', enables navigation blocking
    
    try {
      // Step 1: Transform keywords (expensive operation)
      updateTopicAnalysisState({ message: 'Extracting keywords...' });
      const keywordsForClustering = extractKeywordsForClustering(analysis, (stage) => {
        updateTopicAnalysisState({ message: stage });
      });
      
      const keywordsForAPI = deduplicateKeywords(keywordsForClustering);
      
      // Step 2: Run ML clustering (10-15 seconds)
      updateTopicAnalysisState({ message: 'Running ML clustering...' });
      const topics = await performMLClustering(keywordsForAPI, meta, keywordsForClustering, true); // skipCacheCheck=true
      
      completeTopicAnalysis();
      return topics;
    } catch (error) {
      errorTopicAnalysis(error);
      throw error;
    }
  };

  const handleGSCConnect = async (authData) => {
    // console.log('🔗 [App2] GSC Connect callback received:', authData);
    
    // GSC connection was successful, the app should refresh to get new data
    // Force a refresh of app data by resetting the initialization flag
    initializationAttempted.current = false;
    
    // Re-initialize the app with new GSC connection
    // This will be triggered by the next useEffect cycle
  };

  // 🔄 SYNC ANALYSIS RESULT TO APP DATA
  useEffect(() => {
    // Sync analysis results when available (either summary or complete)
    if (analysisResult && (progressiveState?.stage === 'polling' || isComplete)) {
      setAppData(prev => ({
        ...prev,
        temporaryAnalysis: analysisResult
      }));
    }
  }, [isComplete, analysisResult, progressiveState?.stage]);

  // 🧭 NAVIGATE TO OVERVIEW WHEN SHOULDNAVIGATE FLAG IS SET
  useEffect(() => {
    // Navigate when analysis completes (either cache hit or summary) AND shouldNavigate flag is set
    if (analysisResult && progressiveState?.shouldNavigate) {
      navigate('/overview');
      
      // Clear the navigation flag so we don't navigate again
      clearNavigationFlag();
    }
  }, [analysisResult, progressiveState?.shouldNavigate, navigate, clearNavigationFlag]);

  // 🎨 UI STATE
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [fullWidth, setFullWidth] = useState(() => {
    const saved = localStorage.getItem('fullWidth');
    return saved ? JSON.parse(saved) : false;
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showInitialLoading, setShowInitialLoading] = useState(true);

  // 💬 CHAT STATE
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isChatSidebarCollapsed, setIsChatSidebarCollapsed] = useState(false);
  const [chatMode, setChatMode] = useState('sidebar'); // 'sidebar' or 'overlay'
  const [isChangingProperty, setIsChangingProperty] = useState(false);
  const [isAIPlaygroundOpen, setIsAIPlaygroundOpen] = useState(false);
  const [llmPortalAutoTestData, setLlmPortalAutoTestData] = useState(null);
  const [llmPortalAgentContext, setLlmPortalAgentContext] = useState(null);
  
  // 🎯 AI ANALYSIS COMPLETION STATE for agent context
  const [recentAIAnalyses, setRecentAIAnalyses] = useState({
    promptBurst: null, // { id, timestamp, summary }
    aiCrawler: null    // { id, timestamp, summary }
  });

  // Clear AI analysis context when navigating away from tools
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Clear prompt burst context when leaving /prompt-burst
    if (!currentPath.startsWith('/prompt-burst') && recentAIAnalyses.promptBurst) {
      // console.log('🎯 [App] Clearing Prompt Burst context - user navigated away from tool');
      setRecentAIAnalyses(prev => ({ ...prev, promptBurst: null }));
    }
    
    // Clear AI crawler context when leaving /ai-crawler  
    if (!currentPath.startsWith('/ai-crawler') && recentAIAnalyses.aiCrawler) {
      // console.log('🎯 [App] Clearing AI Crawler context - user navigated away from tool');
      setRecentAIAnalyses(prev => ({ ...prev, aiCrawler: null }));
    }
  }, [location.pathname, recentAIAnalyses.promptBurst, recentAIAnalyses.aiCrawler]);

  // 🎨 UI PREFERENCES PERSISTENCE
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('fullWidth', JSON.stringify(fullWidth));
  }, [fullWidth]);

  // 🎯 GLOBAL SCROLL TO TOP ON NAVIGATION
  useEffect(() => {
    // Scroll to top of main content area when route changes
    if (mainScrollTarget.current) {
      mainScrollTarget.current.scrollIntoView({ 
        behavior: 'instant', 
        block: 'start' 
      });
    }
  }, [location.pathname]);

  // 🔐 EXTRACT APP DATA FROM AUTH RESPONSE - No separate initialization needed!
  useEffect(() => {
    // Only run when auth check is complete and user is authenticated
    if (authStatus.isChecking || !authStatus.isAuthenticated) {
      setAppData(null);
      setAppError(null);
      initializationAttempted.current = false;
      return;
    }

    // If already processed this auth response, don't re-process
    if (initializationAttempted.current) {
      return;
    }

    initializationAttempted.current = true;
    
    // Extract app data from auth response (now included in authStatus.user)
    if (authStatus.user?.app_data) {
      const appData = authStatus.user.app_data;
      const chatState = authStatus.user.chat_state;
      
      // Build complete app state from auth response
      const completeAppData = {
        isConnected: appData.isConnected || false,
        properties: appData.properties || [],
        selectedProperty: appData.lastViewedProperty || (appData.properties?.[0]?.siteUrl) || null,
        temporaryAnalysis: appData.temporaryAnalysis,
        // 🎯 ADD: Backend chat state (single source of truth)
        chatState: chatState || null,
        // Handle GSC errors gracefully
        needsGSCReauth: !appData.isConnected && appData.gscError,
        gscError: appData.gscError
      };

      // Single state update - appData existence indicates completion
      setAppData(completeAppData);
      setAppError(null);
      
      // Start fade out of loading animation
      setTimeout(() => setShowInitialLoading(false), 200);
    } else {
      // Fallback if app_data is missing from auth response
      setAppError('Failed to initialize application - missing app data');
    }
  }, [authStatus.isChecking, authStatus.isAuthenticated, authStatus.user?.app_data]);

  // 🚪 LOGOUT HANDLER
  const handleLogout = () => {
    setAppData(null);
    setAppError(null);
    initializationAttempted.current = false;
    logout();
  };

  // 🎨 UI HANDLERS
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleFullWidth = () => {
    setFullWidth(!fullWidth);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // 💬 CHAT HANDLERS
  const handleChatOpen = () => {
    if (chatMode === 'sidebar') {
      setIsChatSidebarCollapsed(false);
    } else {
      setIsChatOpen(true);
    }
    setHasUnreadMessages(false);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
  };

  const handleChatSidebarToggle = () => {
    setIsChatSidebarCollapsed(!isChatSidebarCollapsed);
  };

  // 🔄 PROPERTY CHANGE HANDLER (for property-scoped chat)
  const handlePropertyChangeForChat = (newPropertyUrl) => {
    // console.log('🔄 [App] Property changed, triggering chat refresh:', newPropertyUrl);
    // Trigger chat refresh by incrementing the refresh trigger
    setChatRefreshTrigger(prev => prev + 1);
  };

  // 🔄 CHAT REFRESH TRIGGER (for portal sync)
  const [chatRefreshTrigger, setChatRefreshTrigger] = useState(0);

  // 🧠 AI PLAYGROUND HANDLERS
  const handleAIPlaygroundOpen = (options = {}) => {
    // If this is a reset operation, clear existing state first
    if (options.resetPortal) {
      setLlmPortalAutoTestData(null);
      setLlmPortalAgentContext(null);
      // Small delay to ensure state is cleared before setting new data
      setTimeout(() => {
        if (options.autoTestData) {
          setLlmPortalAutoTestData(options.autoTestData);
        }
        if (options.agentContext) {
          setLlmPortalAgentContext(options.agentContext);
        }
      }, 10);
    } else {
      // Normal operation - set data directly
      if (options.autoTestData) {
        setLlmPortalAutoTestData(options.autoTestData);
      }
      if (options.agentContext) {
        setLlmPortalAgentContext(options.agentContext);
      }
    }
    setIsAIPlaygroundOpen(true);
  };

  const handleAIPlaygroundClose = () => {
    setIsAIPlaygroundOpen(false);
    setLlmPortalAutoTestData(null); // Clear auto-test data when closing
    setLlmPortalAgentContext(null); // Clear agent context when closing
    
    // Trigger chat refresh to sync any new messages from portal
    setChatRefreshTrigger(prev => prev + 1);
  };

  const handleChatMessage = (message, context) => {
    // console.log('💬 [App] Chat message:', message);
    // console.log('🧠 [App] Smart context:', smartContext);
    // console.log('📍 [App] Context details:', {
    //   type: smartContext?.type,
    //   page: smartContext?.page,
    //   summary: smartContext?.summary,
    //   capabilities: smartContext?.capabilities,
    //   quickStats: smartContext?.utils?.getQuickStats()
    // });
    
    // TODO: Connect to actual chat API
    // The smart context will be sent along with the message to provide
    // intelligent, context-aware responses
  };

  const handleNavigateToAnalysis = (actionType, data) => {
    // console.log('🎯 [App] Navigate action received:', { actionType, data });
    
    if (actionType === 'navigate_to_analysis') {
      // Handle existing analysis navigation
      const { reportType, filters } = data;
      
      const entityType = reportType === 'keywords' ? 'keyword' : 'page';
      const currentRoute = location.pathname;
      const targetRoute = reportType === 'keywords' ? '/keywords' : '/analysis';
      
      // 1. Clear entity-specific filters first to avoid conflicts
      clearAnalysisFiltersForEntity(entityType);
      // console.log('🧹 [App] Cleared existing filters for', entityType, 'analysis');
      
      // 2. Apply new filters
      const storageKeys = getAnalysisStorageKeys(entityType);
      const filterMapping = {
        searchTerm: storageKeys.SEARCH_TERM,
        searchConfig: storageKeys.SEARCH_CONFIG,
        positionFilter: `queryburst-${entityType}-analysis-position-filter`,
        distributionFilter: storageKeys.DISTRIBUTION_FILTER,
        performanceFilter: storageKeys.PERFORMANCE_FILTER,
        pageReportFilter: storageKeys.PAGE_REPORT_FILTER,
        showOnlyTraffic: storageKeys.SHOW_ONLY_TRAFFIC,
        impressionsRange: storageKeys.IMPRESSIONS_RANGE,
        clicksRange: storageKeys.CLICKS_RANGE,
        sortType: storageKeys.SORT_TYPE
      };
      
      Object.entries(filters).forEach(([filterKey, filterValue]) => {
        if (filterValue !== null && filterValue !== undefined) {
          const storageKey = filterMapping[filterKey];
          if (storageKey) {
            // console.log('Setting localStorage:', storageKey, '=', filterValue);
            localStorage.setItem(storageKey, JSON.stringify(filterValue));
            
            // Dispatch storage event manually for same-window updates
            window.dispatchEvent(new StorageEvent('storage', {
              key: storageKey,
              oldValue: null, // We cleared it above
              newValue: JSON.stringify(filterValue),
              storageArea: localStorage
            }));
          }
        }
      });
      
      // 3. Handle navigation vs same-page scenarios
      if (currentRoute === targetRoute) {
        // Same page - filters applied via storage events
      } else {
        // Different page - navigate
        navigate(targetRoute);
      }
    } else if (actionType === 'navigate_to_page') {
      // Handle new page navigation with optional auto-run
      const { page_url, tab, autorun, keyword } = data;
      
      // URL encode the page URL for the route
      const encodedUrl = encodeURIComponent(page_url);
      const targetRoute = `/page-analysis/${encodedUrl}`;
      
      // Add tab parameter if specified
      const tabParam = tab && tab !== 'overview' ? `?tab=${tab}` : '';
      const fullRoute = targetRoute + tabParam;
      
      // If autorun is enabled, pass the auto-fill data via navigation state
      if (autorun && keyword && tab && ['search-intent', 'content-gaps', 'quality-rater', 'helpful-content', 'seo-analysis', 'seo-audit'].includes(tab)) {
        navigate(fullRoute, {
          state: {
            autoFillData: {
              tab: tab,
              keyword: keyword,
              auto_fill: true
            }
          }
        });
      } else {
        navigate(fullRoute);
      }
    } else if (actionType === 'navigate_to_overview') {
      // Handle overview navigation
      navigate('/overview');
    } else if (actionType === 'trigger_analysis') {
      // Handle analysis triggering (new functionality)
      const { analysis_type, date_ranges, country } = data;
      
      // Build form data for the analysis - matching NewAnalysisForm.jsx structure
      const formData = {
        dates: {
          oldStartDate: date_ranges.old_period.start_date,
          oldEndDate: date_ranges.old_period.end_date,
          newStartDate: date_ranges.new_period.start_date,
          newEndDate: date_ranges.new_period.end_date,
          // Add the formattedDates structure that useRunNewAnalysis expects (string format)
          formattedDates: {
            old_period: `${date_ranges.old_period.start_date} - ${date_ranges.old_period.end_date}`,
            new_period: `${date_ranges.new_period.start_date} - ${date_ranges.new_period.end_date}`
          }
        },
        country: country || '',
        useCountryScaling: true,
        collectHistorical: true,
        selectedProperty: appData.selectedProperty
      };
      
      // Trigger the analysis
      handleRunNewAnalysis(formData);
    } else if (actionType === 'navigate_to_prompt_burst') {
      // Handle navigation to Prompt Burst analysis tool
      navigate('/prompt-burst');
    } else if (actionType === 'auto_run_prompt_burst') {
      
      // Prepare auto-test data for Prompt Burst in the same format as LLM Portal
      const autoTestData = {
        primary_prompt: data.primary_prompt,
        persona: data.persona,
        related_url: data.related_url, // Optional agent context
        auto_run: true
      };
      
      // Navigate to Prompt Burst and pass the auto-test data
      // We'll need to modify the Prompt Burst component to accept this data
      navigate('/prompt-burst', { 
        state: { 
          autoTestData 
        } 
      });
    } else if (actionType === 'auto_run_ai_crawler') {
      // Handle auto-run AI Crawler with agent-generated URL
      
      // Prepare auto-run data for AI Crawler
      const autoRunData = {
        target_url: data.target_url,
        related_url: data.related_url, // Optional agent context
        auto_run: true
      };

      // Navigate to AI Crawler with auto-run data
      navigate('/ai-crawler', { 
        state: { 
          autoRunData 
        } 
      });
    } else if (actionType === 'navigate_to_ai_crawler') {
      // Handle navigation to AI Crawler analysis tool
      navigate('/ai-crawler');
    }
  };

  // 🎯 AI ANALYSIS COMPLETION HANDLER
  const handleAIAnalysisComplete = (analysisType, analysisId, summary) => {
    
    setRecentAIAnalyses(prev => ({
      ...prev,
      [analysisType]: {
        id: analysisId,
        timestamp: new Date(),
        summary: summary || `${analysisType} AI analysis completed`
      }
    }));
  };

  // 🔧 UTILITY FUNCTIONS
  const getGravatarUrl = (email) => {
    if (!email) return null;
    const hash = md5(email.trim().toLowerCase());
    return `https://www.gravatar.com/avatar/${hash}?d=mp&s=40`;
  };

  const getDateDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  };

  // 🎯 CHAT STATE ACTIONS INTEGRATION - New backend-driven action handling
  const chatStateActions = useChatStateActions({ 
    openMatrixPortal,
    handleRunNewAnalysis,
    getDateDaysAgo
  });
  
  // 🎯 DEBUG: Monitor chat state actions
  useEffect(() => {
    if (chatStateActions.hasPendingActions) {
      console.log('🎯 [App] Chat state actions detected:', {
        pendingActionCount: chatStateActions.pendingActionCount,
        pendingActionTypes: chatStateActions.pendingActionTypes
      });
    }
  }, [chatStateActions.hasPendingActions, chatStateActions.pendingActionCount]);

  // 🚪 LOGOUT STATE - Show logout overlay with higher priority than loading
  if (authStatus.isLoggingOut) {
    return (
      <div className={`flex items-center justify-center h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={`text-lg ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Logging out...</p>
        </div>
      </div>
    );
  }

  // 🔄 LOADING STATE - Show loading until we have auth AND app data
  if (authStatus.isChecking) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-4">Checking authentication...</p>
      </div>
    );
  }

  // Show loading until we have complete app data OR error (if authenticated)
  if (authStatus.isAuthenticated && (!appData || showInitialLoading) && !appError) {
    return (
      <Suspense fallback={<PageLoadingState darkMode={darkMode} />}>
        <InitialLoading />
      </Suspense>
    );
  }

  // Show error if initialization failed
  if (appError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load application: {appError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }


  // Check if this is a GSC auth popup callback (at base URL with auth params)
  const urlParams = new URLSearchParams(window.location.search);
  const isGSCPopup = urlParams.has('code') || urlParams.has('error');
  const isPopupWindow = window.opener && window !== window.top;
  
  if (isGSCPopup && isPopupWindow) {
    return (
      <Suspense fallback={<PageLoadingState darkMode={darkMode} />}>
        <GSCAuthPopup />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoadingState darkMode={darkMode} />}>
      <ErrorBoundary>
      <Routes>
        {/* Login — primary auth entry point */}
        <Route path="/login" element={
          authStatus.isAuthenticated ? (
            <Navigate to="/home" replace />
          ) : (
            <VerifyLoginScreen
              setAuthStatus={setAuthStatus}
              isAuthenticated={authStatus.isAuthenticated}
            />
          )
        } />
        <Route path="/signup" element={
          authStatus.isAuthenticated ? (
            <Navigate to="/home" replace />
          ) : (
            <SignupScreen isAuthenticated={authStatus.isAuthenticated} />
          )
        } />
        <Route path="/matrix" element={<Navigate to="/home" replace />} />
        <Route path="/public" element={<Navigate to="/home" replace />} />

        <Route path="/auth-callback" element={<AuthCallback />} />

        {/* DaveGPT Demo - Public web.run replica showing how AI search works */}
        <Route path="/davegpt" element={
          <WebRunDemoPage />
        } />

        {/* /answer-engine redirects to the real workspace */}
        <Route path="/answer-engine" element={<Navigate to="/answer-engine/chat" replace />} />

        {/* Answer Engine — Persona chat interface (Pro only) */}
        <Route path="/answer-engine/chat" element={
          !authStatus.isAuthenticated
            ? <Navigate to="/login" replace />
            : authStatus.user?.is_demo_user
              ? <Navigate to="/home?upgrade=pro" replace />
              : <AnswerEngineShell />
        }>
          <Route index element={<WebsiteListPage />} />
          <Route path=":websiteId" element={<PersonaListPage />} />
          <Route path=":websiteId/:personaId" element={<PersonaChatPage />} />
          <Route path=":websiteId/:personaId/stats" element={<PersonaChatPage />} />
          <Route path=":websiteId/:personaId/stats/report" element={<StatsReport />} />
          <Route path=":websiteId/:personaId/:conversationId" element={<PersonaChatPage />} />
        </Route>

        <Route path="/subscription/success" element={
          <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <SubscriptionSuccess darkMode={darkMode} />
          </div>
        } />
        
        {/* GSC Auth Popup Route */}
        <Route path="/gsc-auth-popup" element={<GSCAuthPopup />} />
        
        {/* /verify → redirect to /home (backward compat) */}
        <Route path="/verify" element={<Navigate to="/home" replace />} />

        {/* Home — landing page with panels for Verify, GSC, Quick Start */}
        <Route path="/home" element={
          authStatus.isAuthenticated ? (
            <AppShell />
          ) : (
            <Navigate to="/login" replace />
          )
        }>
          <Route index element={<HomePage />} />
        </Route>

        {/* Site selector — AppShell with no siteId (Pro only) */}
        <Route path="/sites" element={
          authStatus.isAuthenticated ? (
            <ProGate authStatus={authStatus}>
              <AppShell />
            </ProGate>
          ) : (
            <Navigate to="/login" replace />
          )
        }>
          {siteSelectorRoute}
        </Route>

        {/* Account — subscription & integrations within AppShell */}
        <Route path="/account" element={
          authStatus.isAuthenticated ? (
            <AppShell />
          ) : (
            <Navigate to="/login" replace />
          )
        }>
          <Route index element={<SubscriptionPage />} />
          <Route path="integrations" element={<FullPlatformGate authStatus={authStatus}><IntegrationsPage /></FullPlatformGate>} />
          <Route path="agent-serve" element={<FullPlatformGate authStatus={authStatus}><AgentServePage /></FullPlatformGate>} />
        </Route>

        {/* Search Console Explorer — standalone agentic page (Pro only) */}
        <Route path="/search-console" element={
          authStatus.isAuthenticated ? (
            <ProGate authStatus={authStatus}>
              <AppShell />
            </ProGate>
          ) : (
            <Navigate to="/login" replace />
          )
        }>
          <Route index element={<SearchConsolePage />} />
          <Route path="report" element={<GscReport />} />
        </Route>
        
        {/* Legacy onboard routes → redirect to /sites */}
        <Route path="/verify/onboard/*" element={<Navigate to="/home" replace />} />

        {/* Page Details - Standalone page details view (Pro only) */}
        <Route path="/verify/page/:pageId" element={
          authStatus.isAuthenticated ? (
            <ProGate authStatus={authStatus}>
              <PageDetailsPage />
            </ProGate>
          ) : (
            <Navigate to="/login" replace />
          )
        } />

        {/* Verify app — route-based navigation (Pro only) */}
        <Route path="/site/:siteId" element={
          authStatus.isAuthenticated ? (
            <ProGate authStatus={authStatus}>
              <AppShell />
            </ProGate>
          ) : (
            <Navigate to="/login" replace />
          )
        }>
          {verifyChildRoutes}
        </Route>
        
        {/* Root → /home so authenticated users land on the home page */}
        <Route path="/" element={
          authStatus.isAuthenticated
            ? <Navigate to="/home" replace />
            : <Navigate to="/login" replace />
        } />

        {/* Protected Routes - Only render when we have complete app data */}
        <Route path="/*" element={
          authStatus.isAuthenticated && isAnswerEngineOnly(authStatus.user) ? (
            <Navigate to="/home" replace />
          ) : authStatus.isAuthenticated && appData ? (
            <div className={`h-screen flex flex-col ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
              {/* Sidebar */}
              <Sidebar
                isOpen={isSidebarOpen}
                onClose={closeSidebar}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                fullWidth={fullWidth}
                toggleFullWidth={toggleFullWidth}
                authStatus={authStatus}
                handleLogout={handleLogout}
                getGravatarUrl={getGravatarUrl}
                gscData={{
                  isConnected: appData.isConnected,
                  properties: appData.properties,
                  isLoading: false,
                  error: null
                }}
                selectedProperty={appData.selectedProperty}
                onPropertyChange={async (newProperty) => {
                  try {
                    await handlePropertyChange(newProperty);
                    setIsSidebarOpen(false);
                  } catch (error) {
                    // Still close sidebar even on error
                    setIsSidebarOpen(false);
                  }
                }}
                progressiveAnalysisState={progressiveState}
                isAnalysisRunning={isRunning}
                topicAnalysisState={topicAnalysisState}
                onAIPlaygroundOpen={handleAIPlaygroundOpen}
                onPropertiesRefresh={(data) => {
                  setAppData(prev => ({
                    ...prev,
                    isConnected: data.isConnected ?? prev.isConnected,
                    properties: data.properties || prev.properties,
                    selectedProperty: data.lastViewedProperty || prev.selectedProperty,
                  }));
                }}
              />

              {/* Mobile Header */}
              <MobileHeader 
                darkMode={darkMode}
                onMenuToggle={() => setIsSidebarOpen(true)}
              />

              {/* Main Content with sidebar spacing  */}
              <div className={`
                md:pl-64 flex-1 flex flex-col pt-16 md:pt-0 overflow-hidden
                ${chatMode === 'sidebar' && !isChatSidebarCollapsed ? 'md:pr-80' : 'md:pr-0'}
                ${darkMode ? 'dark bg-gray-900' : 'bg-gray-100/75'}
              `}>
                {/* Global Demo Banner for demo users - hidden on home/pricing */}
                {authStatus.user?.is_demo_user && location.pathname !== '/home' && (
                  <Suspense fallback={null}>
                    <DemoBanner 
                      darkMode={darkMode} 
                      onUpgrade={() => window.location.href = '/home'}
                    />
                  </Suspense>
                )}

                {/* Main Content Routes */}
                <main className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-1 overflow-y-auto scrollbar-modern">
                    {/* 🎯 GLOBAL SCROLL TARGET - Invisible div for scroll-to-top functionality */}
                    <div ref={mainScrollTarget} className="h-0" />
                    <Routes>

                    {/* Dashboard Route - Now handled by AnalysisLayer */}
                    <Route path="/dashboard" element={
                      <AnalysisLayer
                        selectedProperty={appData.selectedProperty}
                        gscData={{
                          isConnected: appData.isConnected,
                          properties: appData.properties,
                          isLoading: false,
                          error: null
                        }}
                        authStatus={authStatus}
                        temporaryAnalysis={appData.temporaryAnalysis}
                        progressiveAnalysisState={progressiveState}
                        darkMode={darkMode}
                        fullWidth={fullWidth}
                        isDemoUser={authStatus.user.is_demo_user || false}
                        onLoadSavedAnalysis={handleLoadSavedAnalysis}
                        onRunNewAnalysis={handleRunNewAnalysis}
                        onClearAnalysis={handleClearAnalysis}
                        // Topic analysis props
                        topicAnalysisState={topicAnalysisState}
                        onRunTopicAnalysis={runTopicAnalysis}
                        // Special prop to indicate dashboard mode
                        dashboardMode={true}
                      />
                    } />
                    
                    {/* Performance Layer - Historical Reports */}
                    <Route path="/performance/*" element={
                      <PerformanceLayer
                        darkMode={darkMode}
                        authStatus={authStatus}
                        fullWidth={fullWidth}
                      />
                    } />
                    
                    {/* Subscription Details */}
                    <Route path="/subscription" element={
                      <SubscriptionDetails
                        darkMode={darkMode}
                      />
                    } />
                    
                    {/* Pricing — redirect to /home which handles free vs Pro */}
                    <Route path="/pricing" element={<Navigate to="/home" replace />} />
                    
                    {/* Tools Layer */}
                    <Route path="/tools/*" element={
                      <ToolsLayer
                        darkMode={darkMode}
                      />
                    } />
                    

                    
                    
                    {/* Focus Route - Clean decision interface */}
                    <Route path="/focus" element={
                      <Focus darkMode={darkMode} />
                    } />
                    
                    {/* Domain Profile Route - Business intelligence management */}
                    <Route path="/domain-profile" element={
                      <DomainProfilePage darkMode={darkMode} />
                    } />
                    
                    {/* Research Intelligence - AI Tools */}
                    <Route path="/research-intelligence" element={
                      <ResearchIntelligencePage />
                    } />
                    
                    {/* Prompt Burst - AI-Powered Research Intelligence */}
                    <Route path="/prompt-burst" element={
                      <ResearchIntelligenceV2Page onAIAnalysisComplete={handleAIAnalysisComplete} />
                    } />
                    
                    {/* AI Crawler - AI Content Intelligence */}
                    <Route path="/ai-crawler" element={
                      <WebConceptExtractorPage darkMode={darkMode} onAIAnalysisComplete={handleAIAnalysisComplete} />
                    } />
                    
                    {/* Content Strategy - Modern Content Pipeline */}
                    <Route path="/content-strategy" element={
                      <ContentStrategyV2 darkMode={darkMode} />
                    } />
                    
                    {/* Article Editor for Content Strategy */}
                    <Route path="/content-strategy/article/:id/edit" element={
                      <ArticleEditor darkMode={darkMode} />
                    } />
                    
                    {/* Default Analysis Layer */}
                    <Route path="/*" element={
                      <AnalysisLayer
                        selectedProperty={appData.selectedProperty}
                        gscData={{
                          isConnected: appData.isConnected,
                          properties: appData.properties,
                          isLoading: false,
                          error: null
                        }}
                        authStatus={authStatus}
                        temporaryAnalysis={appData.temporaryAnalysis}
                        progressiveAnalysisState={progressiveState}
                        darkMode={darkMode}
                        fullWidth={fullWidth}
                        isDemoUser={authStatus.user.is_demo_user || false}
                        onLoadSavedAnalysis={handleLoadSavedAnalysis}
                        onRunNewAnalysis={handleRunNewAnalysis}
                        onClearAnalysis={handleClearAnalysis}
                        // Topic analysis props
                        topicAnalysisState={topicAnalysisState}
                        onRunTopicAnalysis={runTopicAnalysis}
                      />
                    } />
                  </Routes>
                  </div>
                </main>
              </div>

              {/* 💬 CHAT SIDEBAR - Responsive positioning */}
              {chatMode === 'sidebar' && (
                <div className={`
                  fixed z-30 transition-all duration-300
                  ${isChatSidebarCollapsed 
                    ? 'bottom-4 right-4 w-12 h-12 rounded-full md:top-0 md:right-0 md:h-full md:w-12 md:rounded-none md:bottom-auto' 
                    : 'bottom-0 left-0 right-0 h-1/2 md:top-0 md:right-0 md:left-auto md:h-full md:w-80'
                  }
                `}>
                  <ChatSidebar
                    darkMode={darkMode}
                    currentContext={smartContext}
                    onSendMessage={handleChatMessage}
                    onNavigateToAnalysis={handleNavigateToAnalysis}
                    isCollapsed={isChatSidebarCollapsed}
                    onToggleCollapse={handleChatSidebarToggle}
                    analysisData={appData?.temporaryAnalysis?.analysis}
                    progressiveAnalysisState={progressiveState}
                    isAnalysisRunning={isRunning}
                    onLaunchLLMPortal={handleAIPlaygroundOpen}
                    refreshTrigger={chatRefreshTrigger}
                    recentAIAnalyses={recentAIAnalyses}
                    historicalDataPolling={historicalDataPolling}
                    isChangingProperty={isChangingProperty}
                    currentDomain={(() => {
                      // 1. BEST: Extract domain from actual page URLs in analysis data
                      if (appData?.temporaryAnalysis?.analysis) {
                        const pages = Object.keys(appData.temporaryAnalysis.analysis);
                        if (pages.length > 0) {
                          try {
                            const firstPageUrl = pages[0];
                            const domain = new URL(firstPageUrl).hostname.replace(/^www\./, '');
                            return domain;
                          } catch {
                            // Continue to fallbacks if URL parsing fails
                          }
                        }
                      }
                      // 2. Parse GSC property manually
                      if (appData?.selectedProperty) {
                        return appData.selectedProperty
                          .replace(/^https?:\/\//, '')
                          .replace(/^sc-domain:/, '')
                          .replace(/\/$/, '')
                          .replace(/^www\./, '');
                      }
                      // 3. LAST RESORT: Use meta domain (likely GSC property format)
                      if (appData?.temporaryAnalysis?.meta?.domain) {
                        return appData.temporaryAnalysis.meta.domain
                          .replace(/^https?:\/\//, '')
                          .replace(/^sc-domain:/, '')
                          .replace(/\/$/, '')
                          .replace(/^www\./, '');
                      }
                      return null;
                    })()}
                    analysisSettings={{
                      temporaryAnalysis: appData?.temporaryAnalysis,
                      isDemoUser: authStatus.user?.is_demo_user || false,
                      isViewingSavedAnalysis: false, // TODO: Track this in app state if needed
                      refreshTrigger: 0, // TODO: Add refresh tracking if needed
                      isAnalysisRunning: isRunning,
                      progressiveAnalysisState: progressiveState,
                      // Analysis handlers - these will be implemented by creating a bridge to AnalysisLayer
                      onSave: () => {
                        // For now, just trigger a reset - TODO: implement proper save flow
                        handleRunNewAnalysis({ reset: true });
                      },
                      onReset: () => {
                        // Trigger a reset analysis
                        handleRunNewAnalysis({ reset: true });
                      }, 
                      onLoadSavedAnalysis: handleLoadSavedAnalysis,
                      onRunAgain: (historyItem) => {
                        // Convert history item format and run analysis
                        const analysisParams = {
                          selectedProperty: historyItem.property,
                          dates: {
                            formattedDates: {
                              old_period: `${historyItem.period_1.start} - ${historyItem.period_1.end}`,
                              new_period: `${historyItem.period_2.start} - ${historyItem.period_2.end}`
                            }
                          },
                          country: historyItem.country || '',
                          useCountryScaling: historyItem.use_country_scaling || false,
                          collectHistorical: false // Don't collect historical for re-runs
                        };
                        handleRunNewAnalysis(analysisParams);
                      },
                      onRunNewAnalysis: handleRunNewAnalysis
                    }}
                    // Property-scoped chat props
                    propertyUrl={appData?.selectedProperty}
                    onPropertyChange={handlePropertyChangeForChat}
                  />
                </div>
              )}

              {/* 🧠 AI PLAYGROUND PORTAL */}
              {isAIPlaygroundOpen && (
                <Suspense fallback={null}>
                  <LLMPortal 
                    darkMode={darkMode}
                    standalone={true}
                    onClose={handleAIPlaygroundClose}
                    autoTestData={llmPortalAutoTestData}
                    agentContext={llmPortalAgentContext}
                    onNavigateToAnalysis={handleNavigateToAnalysis}
                  />
                </Suspense>
              )}

              {/* Global Scroll To Top Button */}
              <ScrollToTopButton 
                darkMode={darkMode} 
                chatMode={chatMode}
                isChatSidebarCollapsed={isChatSidebarCollapsed}
              />
            </div>
          ) : (
            <Navigate to="/login" replace />
          )
        } />
      </Routes>
      </ErrorBoundary>
    </Suspense>
  );
}

// Main App Component with AuthProvider
export default function App2() {
  return (
    <ChatStateProvider>
      <AuthProvider2>
        <ChatProvider>
          <AppContent />
        </ChatProvider>
      </AuthProvider2>
    </ChatStateProvider>
  );
} 